<template>
  <form @submit.prevent="save">
    <b-message type="is-danger" v-if="errors.length > 0">
      <p v-for="error in errors">{{error}}</p>
    </b-message>
    <div class="columns is-multiline">
      <div class="column is-4">
        <b-field>
          <form-select :label="`${$t('docflow.fields.type')} *`" v-model="form.type" :disabled="isChangeRequest">
            <option v-for="type in types" :value="type.id">{{ type.name }}</option>
          </form-select>
        </b-field>
      </div>
      <div class="column is-2" v-if="!form.is_informational">
        <b-field>
          <form-datepicker :label="$t('docflow.fields.term_date')" :min-date="minDate"
                           v-model="form.term_date"></form-datepicker>
        </b-field>
      </div>
      <div class="column is-12">
        <b-field>
          <form-textarea :label="`${$t('interface.description')} *`" v-model="form.description"></form-textarea>
        </b-field>
      </div>
      <div class="column is-4">
        <EmployeeSelect :initials="form.approver__initials"
                        :disabled="isChangeRequest"
                        :scope-all="true"
                        :label="$t('docflow.approver')"
                        v-model="form.approver">
        </EmployeeSelect>
      </div>
      <div class="column is-4">
        <EmployeeSelect :initials="form.supervisor__initials"
                        :scope-all="true"
                        :label="$t('docflow.fields.supervisor')"
                        :disabled="isChangeRequest"
                        v-model="form.supervisor">
        </EmployeeSelect>
      </div>
      <div class="column is-4">
        <EmployeeSelect :initials.sync="form.maintainer__initials"
                        :scope-all="true"
                        :label="$t('projects.maintainer')"
                        v-model="form.maintainer">
        </EmployeeSelect>
      </div>
      <div class="column is-4">
        <EmployeeSelect :initials.sync="form.lawyer__initials"
                        :scope-all="true"
                        :label="$t('docflow.fields.lawyer')"
                        v-model="form.lawyer">
        </EmployeeSelect>
      </div>
      <div class="column is-4">
        <EmployeeSelect :initials.sync="form.spotter__initials"
                        :scope-all="true"
                        :label="$t('docflow.fields.spotter')"
                        v-model="form.spotter">
        </EmployeeSelect>
      </div>
      <div class="column is-4">
        <DocumentSelect :name="form.parent__registration_number"
                        :label="$t('docflow.parentDocument')"
                        :disabled="isChangeRequest"
                        v-model="form.parent">
        </DocumentSelect>
      </div>
      <div class="column is-12 mt-10">
        <b-checkbox v-model="form.is_informational" :disabled="isChangeRequest">
          {{ $t('docflow.fields.is_informational') }}
        </b-checkbox>
        <b-checkbox v-model="form.is_classified" :disabled="isChangeRequest" class="ml-40">
          {{ $t('docflow.fields.is_classified') }}
        </b-checkbox>
      </div>
      <div class="column is-12 mt-5" v-if="!form.is_classified">
        <b-field class="file">
          <b-upload v-model="form.file" :disabled="isChangeRequest">
            <a class="button is-warning" :class="{'is-static': isChangeRequest}">
              <span>{{ $t('docflow.fields.file') }} *</span>
            </a>
          </b-upload>
          <span class="file-name">
            <template v-if="form.file">{{ form.file.name }}</template>
            <template v-if="form.file__name && !form.file">{{ form.file__name }}</template>
            <template v-if="!form.file__name && !form.file">{{ $t('interface.noFileSelected') }}</template>
          </span>
        </b-field>
        <div class="templatesFiles">
          <a target="_blank" href="/files/template_for_all_documents.docx">{{ $t('interface.allDocumentsTemplate')
            }}</a>
          <a target="_blank" href="/files/template_for_order.docx">{{ $t('interface.orderTemplate') }}</a>
        </div>
      </div>
      <div class="column is-12">
        <div class="field is-grouped mt-10">
          <div class="control">
            <button type="submit" class="button is-primary">{{ $t('interface.save') }}</button>
          </div>
          <div class="control">
            <button class="button is-danger" @click.prevent="cancel">{{ $t('interface.cancel') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import {prepareDate} from '@/utils'
  import EmployeeSelect from '@/components/EmployeeSelect'
  import DocumentSelect from '@/components/DocumentSelect'
  import GovernmentAgencySelect from '@/components/GovernmentAgencySelect'
  import moment from 'moment'

  const defaultDocumentData = {
    description: '',
    type: '',
    term_date: new Date(),
    file: null,
    is_informational: false,
    maintainer: null,
    approver: null,
    supervisor: null,
    spotter: null,
    lawyer: null,
    parent: null,
    is_classified: false
  }

  export default {
    props: ['documentData', 'editMode'],
    components: {
      EmployeeSelect,
      GovernmentAgencySelect,
      DocumentSelect
    },
    data() {
      return {
        form: this.documentData || {...defaultDocumentData},
        isEdit: this.editMode || false,
        errors: [],
        minDate: moment().subtract(1, 'days').toDate(),
        maxDate: new Date()
      }
    },
    methods: {
      validate() {
        this.errors = []
        if (this.form.description === '' || !this.form.maintainer) {
          this.errors.push(this.$t('interface.requiredFields'))
        }
        return this.errors.length === 0
      },
      save() {
        if (this.validate()) {
          this.$emit('save', {
            ...this.form,
            term_date: this.form.term_date ? prepareDate(this.form.term_date) : null
          })
        }
      },
      cancel() {
        this.$router.go(-1)
      },
    },
    computed: {
      ...mapState('Docflow', ['types', 'deliveryTypes']),
      isChangeRequest() {
        return this.form.change_state ? this.form.change_state === 'approved' : false;
      }
    },
    watch: {
      documentData(document) {
        this.form = document
      }
    }
  }
</script>

<style lang="scss" scoped>
  .templatesFiles {
    float: left;
    margin-left: 10px;
    position: relative;
    top: -4px;

    a {
      display: block;
      color: #0f81cc;
      text-decoration: underline;
    }
  }

  .field.file {
    float: left;
  }
</style>
